// A deploy replaces the app's hashed files. A tab opened before it still points at the old ones, so the
// next screen it loads (a lazy page) comes back as HTML instead of JavaScript and fails. Reloading
// fetches the new build.

const KEY = 'mybooks.chunkReloadAt';
/** A second failure within this window is a real fault, not a stale tab: show the error instead of reloading again. */
const WINDOW_MS = 30_000;

/** True for the error the browser raises when a lazily loaded file cannot be fetched. */
export const isChunkLoadError = (e: unknown): boolean =>
  e instanceof Error && /dynamically imported module|importing a module script failed|ChunkLoadError/i.test(`${e.name} ${e.message}`);

type Store = Pick<Storage, 'getItem' | 'setItem'>;

/** Reload the page once for a new version. False when it already did so a moment ago (no reload loop). */
export function reloadForNewVersion(reload: () => void = () => window.location.reload(), now = Date.now(), store?: Store): boolean {
  try {
    const s = store ?? window.sessionStorage;
    const last = Number(s.getItem(KEY));
    if (last && now - last < WINDOW_MS) return false;
    s.setItem(KEY, String(now));
  } catch {
    // Without storage a loop cannot be ruled out, so do not reload.
    return false;
  }
  reload();
  return true;
}

/** Reload when Vite reports that a lazy file failed to load. */
export function installChunkReload() {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewVersion()) event.preventDefault();
  });
}
